/**
 * privacy-edit.js — platform-privacy 隱私政策編輯頁的「發布／草稿意圖」接線（Pilot②，修黑洞②）
 *
 * 黑洞②現況：整頁原 0 個 <form>，發布鈕只開 modal、確認鈕無 handler →
 *   文件標題 / 生效日期 / 適用語系 / 版本摘要 / 政策內文可空白發布對外政策。
 *
 * 修法分工：
 *   - HTML：以 <form class="admin-privacy-form" id="privacy-edit-form"> 包住 .privacy-layout，
 *     發布確認鈕（#publish-modal 的 [data-action=publish-confirm]）改 type=submit + form= 關聯。
 *     欄位掛 data-rules（文件標題 no-blank；版本摘要 conditional-required；政策內文 richtext-nonempty，
 *     後兩者 data-rule-when 指向「發布」radio → 發布態才必填、草稿態放行）。
 *   - admin-form-edit.js：新增 .admin-privacy-form 的 FORM_CONFIGS 條目（齊驗 / summary / inline /
 *     toast / dirty 全套），submit 經 form= 關聯鈕觸發。
 *   - 本檔：補「意圖同步」這層 admin-form-edit.js 不管的頁面語義——
 *       (1) 按「確定發布」＝明確發布意圖 → submit 前把 publishStatus 設為「發布」（讓條件式必填生效），
 *           並關閉發布 modal（否則驗證失敗時 summary / inline 被 modal 蓋住看不到）。
 *       (2) 按「儲存草稿」＝明確草稿意圖 → 設 publishStatus 為「草稿」+ 清 dirty + 草稿 toast
 *           （草稿態下條件式必填放行，可空白存檔）。save-draft 鈕在 page-head / footer、表單外，
 *           不在引擎 draftBtnSel（form 內）掃描範圍，故於此頁接。
 *
 * 為何「確定發布」要強制 publishStatus=發布：
 *   「發布新版本」這個動作本身就是發布，不該停在草稿態。把 radio 與動作對齊，避免
 *   「按發布卻因 radio 停在草稿 → 條件式必填不生效 → 空白政策過關」的語義漏洞。
 *   （此語義為工程判斷，若業務要「radio 與發布鈕各自獨立」請回報調整——見交付說明。）
 *
 * 提交成功的 toast / spinner / 鎖頁與否由 admin-form-edit.js 的 FORM_CONFIGS 控（本頁
 * lockOnSuccess=false：政策編輯為可重複編輯頁，發布後不鎖整頁）。本檔不重複處理成功路徑。
 *
 * auto-init；無 #privacy-edit-form 即 no-op（不影響其他 admin 頁）。
 */

const FORM_ID = 'privacy-edit-form';

/* 設定發布狀態 radio（draft | publish）。找不到對應 radio 即略過（不硬崩）。 */
function setPublishStatus(form, value) {
  const radio = form.querySelector(`input[name="publishStatus"][value="${value}"]`);
  if (radio && !radio.checked) {
    radio.checked = true;
    // 觸發 change 讓引擎 dirty 追蹤 / 任何即時驗證跟上（與使用者手動點選等價）。
    radio.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

function initPrivacyEdit(root = document) {
  const form = root.getElementById
    ? root.getElementById(FORM_ID)
    : root.querySelector(`#${FORM_ID}`);
  if (!form) return; // 非本頁 → no-op

  // ── 發布確認鈕（type=submit，form= 關聯）：submit 前同步意圖 + 關 modal ──
  // 用 capture 確保早於引擎 submit handler 之前把 publishStatus 設為「發布」，
  // 讓條件式必填（版本摘要 / 政策內文）在這次提交確實生效。click 不 preventDefault →
  // 原生 submit 照常觸發 → 引擎接手齊驗。
  const publishConfirmBtn = document.querySelector(
    `button[type="submit"][form="${FORM_ID}"][data-action="publish-confirm"]`
  );
  if (publishConfirmBtn) {
    publishConfirmBtn.addEventListener(
      'click',
      () => {
        setPublishStatus(form, 'publish');
        // 關閉發布 modal，讓驗證失敗時頂部 summary / 欄位 inline 可見（否則被 modal 蓋住）。
        // 成功時引擎走 toast，modal 已關不影響。
        if (window.Modal && typeof window.Modal.close === 'function') {
          window.Modal.close('#publish-modal');
        }
      },
      true
    );
  }

  // ── 儲存草稿鈕（page-head / footer，表單外）：草稿意圖 + 清 dirty + toast ──
  // 草稿態下 conditional-required / richtext-nonempty 放行（data-rule-when 未命中），可空白存檔。
  // 不走引擎 submit（引擎 draftBtnSel 只掃 form 內鈕；此處鈕在 form 外），於此頁自接 demo 行為。
  document.querySelectorAll('[data-action="save-draft"]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      if (form.classList.contains('is-submitting')) return;
      setPublishStatus(form, 'draft');
      // 清 dirty（與引擎 clearDirty 等價：引擎讀 form.dataset.dirty === 'true'）→ 存後不再攔離開。
      delete form.dataset.dirty;
      if (window.AdminToast && typeof window.AdminToast.show === 'function') {
        window.AdminToast.show({
          type: 'success',
          title: '草稿已儲存', // 需文案（TODO: rone）
          text: '您可稍後回來繼續編輯。', // 需文案（TODO: rone）
        });
      }
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initPrivacyEdit(), { once: true });
} else {
  initPrivacyEdit();
}

export { initPrivacyEdit };
